// The desktop preview of the touch picker (ART-266). On a phone, `Picker` opens its options in a bottom sheet; a
// desktop browser has a fine pointer, so shrinking the window never shows it. With this on, a window narrower than
// `lg` gets the sheet too — so the mobile dropdown can be reviewed without a device.
//
// NON-PRODUCTION ONLY, and two ways to turn it on:
//   - Settings › Account › "Touch dropdowns" (a Switch beside the dark-theme preview), and
//   - a LAUNCH param: open any URL with `?touchPickers=1` (or `=0` to turn it off) — handy for a shared link or an
//     automated browser. The param is remembered, so it survives navigation and reloads.
// Stored in a small non-auth cookie for the same reason the theme preference is: localStorage is lint-banned.
import { create } from "zustand";
import { setPickerSheetPreview } from "@cntrl/ui";
import { IS_NON_PRODUCTION } from "../config/env.js";

const COOKIE_NAME = "cntrl_touch_pickers";
const LAUNCH_PARAM = "touchPickers";

/** True where the preview may be switched on at all. */
export const TOUCH_PICKER_PREVIEW_AVAILABLE = IS_NON_PRODUCTION;

function readStored(): boolean {
  if (!IS_NON_PRODUCTION || typeof document === "undefined") return false;
  return /(?:^|;\s*)cntrl_touch_pickers=1(?:;|$)/.test(document.cookie);
}

function writeStored(on: boolean): void {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=${on ? "1" : "0"}; path=/; max-age=31536000; samesite=lax`;
}

interface TouchPickerPreviewState {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
}

export const useTouchPickerPreview = create<TouchPickerPreviewState>((set) => ({
  enabled: readStored(),
  setEnabled: (on) => {
    if (!IS_NON_PRODUCTION) return;
    writeStored(on);
    setPickerSheetPreview(on);
    set({ enabled: on });
  },
}));

/** At boot: honour a `?touchPickers=` launch param (remembering it), then hand the stored value to `@cntrl/ui`. */
export function applyTouchPickerPreviewAtBoot(): void {
  if (!IS_NON_PRODUCTION || typeof window === "undefined") return;
  const param = new URLSearchParams(window.location.search).get(LAUNCH_PARAM);
  if (param === "1" || param === "0") {
    useTouchPickerPreview.getState().setEnabled(param === "1");
    return;
  }
  setPickerSheetPreview(useTouchPickerPreview.getState().enabled);
}
